import { FormLayout as F, ZodForm, fieldTypes } from "@components/forms/lib/ZodForm"
import { LoadingResults } from "@components/layout/LoadingResults"
import { useMyBilling, useMyInfo, useMyInfoMutation } from "@hooks/queries"
import { Fragment, useMemo } from "react"
import { z } from "zod"

const myDetailsSchema = z.object({
  name: fieldTypes.fullName,
  email: z.string().email().describe("Billing email"),
  phone: fieldTypes.phone.describe("Billing phone"),
  address: fieldTypes.address,
})

type Schema = z.infer<typeof myDetailsSchema>

type Props = {
  mode: "billing" | "shipping"
  requireAddress?: boolean
  prefill?: boolean
}

export function useMyInfoForm(props: Props) {
  const myDetails = useMyBilling(undefined, {
    refetchOnWindowFocus: false,
  })

  const update = useMyInfoMutation()

  const customer = myDetails.data?.customer
  const info = customer?.[props.mode]

  const schema = useMemo(() => {
    return z.object({
      name: fieldTypes.fullName,
      email: z
        .string()
        .email()
        .describe(props.mode === "billing" ? "Billing Email" : "Email"),
      phone: fieldTypes.phone.describe(props.mode === "billing" ? "Billing Phone" : "Phone"),
      address: props.requireAddress ? fieldTypes.addressRequired : fieldTypes.address,
    }) as typeof myDetailsSchema
  }, [props.requireAddress])

  return {
    schema: schema,
    loading: myDetails.isLoading,
    submitting: update.isPending,
    data: {
      name: {
        first: info?.firstName ? info?.firstName : ((props.prefill ? customer?.firstName : "") ?? ""),
        last: info?.lastName ? info?.lastName : ((props.prefill ? customer?.lastName : "") ?? ""),
      } as any,
      email: info?.email ? info.email : props.prefill ? (customer?.email ?? "") : "",
      phone: info?.phone ? (info.phone as any) : ((props.prefill ? (customer?.shipping?.phone ?? customer?.billing?.phone ?? "") : "") as any),
      address: {
        address1: info?.address1 ?? "",
        address2: info?.address2 ?? "",
        city: info?.city ?? "",
        country: info?.country ?? ("" || "AU"),
        postcode: info?.postcode ?? "",
        state: info?.state ?? "",
      } as any,
    },
    update: (data: Schema) => {
      return update.mutateAsync({
        email: data.email,
        firstName: data.name.first,
        lastName: data.name.last,
        [props.mode]: {
          address1: data.address.address1,
          address2: data.address.address2,
          city: data.address.city,
          country: data.address.country as any,
          phone: data.phone,
          postcode: data.address.postcode,
          state: data.address.state,
        },
      })
    },
    render: (fields: Record<keyof Schema, JSX.Element>) => (
      <F.Stack>
        {fields.name}
        <F.Grid>
          {fields.email}
          {fields.phone}
        </F.Grid>
        {fields.address}
      </F.Stack>
    ),
  }
}

export function MyInfoForm(props: Props) {
  const form = useMyInfoForm(props)

  if (form.loading) {
    return <LoadingResults />
  }

  return (
    <Fragment>
      <ZodForm
        schema={form.schema}
        data={form?.data}
        submitLabel="Save Changes"
        loading={form?.submitting}
        onSubmit={(data) => form?.update(data)}
        render={(data) => (
          <>
            {form?.render(data)}
            <F.SubmitBar />
          </>
        )}
      />
    </Fragment>
  )
}
